import type { UserType } from "@/interfaces";
import vendorUserService, { UpdateVendorUserRequest } from "@/services/vendorUserService";
import { Button, Form, Input, message, Modal } from "antd";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  vendorId: string;
  user: UserType;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * EditVendorUserModal component for editing an existing vendor user.
 *
 * Mirrors CreateVendorUserModal, without the vendor selection field.
 */
export default function EditVendorUserModal({ open, vendorId, user, onCancel, onSuccess }: Props) {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  // Populate form fields when modal opens or the selected user changes
  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || "",
        designation: user.designation || "",
        linkedinId: user.linkedinId || "",
      });
    }
  }, [open, user, form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleFormSubmit = async (values: UpdateVendorUserRequest) => {
    setIsSaving(true);
    try {
      const payload: UpdateVendorUserRequest = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || null,
        designation: values.designation || null,
        linkedinId: values.linkedinId || null,
      };

      const response = await vendorUserService.update(vendorId, user.id, payload);
      message.success(response.message || "Vendor user updated successfully");
      onSuccess();
      handleCancel();
    } catch (error: any) {
      console.error("Error updating vendor user:", error);
      message.error(error?.response?.data?.message || error?.message || "Failed to update vendor user");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={
        <div className="mb-4">
          <span style={{ fontSize: 24, fontWeight: 700 }}>Edit Vendor User</span>
        </div>
      }
      centered
      open={open}
      onCancel={handleCancel}
      width={600}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="user@example.com" />
        </Form.Item>

        <Form.Item name="phone" label="Phone (Optional)">
          <Input placeholder="+1234567890" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="John" />
          </Form.Item>

          <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Please enter last name" }]}>
            <Input placeholder="Doe" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="designation" label="Designation (Optional)">
            <Input placeholder="Manager" />
          </Form.Item>

          <Form.Item name="linkedinId" label="LinkedIn URL (Optional)">
            <Input placeholder="https://linkedin.com/in/username" />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isSaving}>
            Update User
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
